/**
 * Styles for the Assessment Modal components
 * Using the Business Resilience Navigator color scheme:
 * - #243444 (Dark blue)
 * - #D2BA7B (Gold)
 * - #FFFAFA (Off-white)
 * - #4C5454 (Gray)
 * - #7C1227 (Red for delete buttons)
 */

export const styles = {
  // Modal container styles
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#FFFAFA',
    borderRadius: '8px',
    padding: '25px',
    width: '90%',
    maxWidth: '1200px',
    maxHeight: '95vh',
    minHeight: '700px',
    overflow: 'auto',
    position: 'relative',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#243444',
  },
  
  // Form styles
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    height: 'calc(100% - 60px)', // Account for header and padding
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '15px',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#243444',
  },
  input: {
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
  },
  select: {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
  },
  
  // Button styles
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '30px',
  },
  button: {
    padding: '10px 20px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  primaryButton: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
  },
  secondaryButton: {
    backgroundColor: '#4C5454',
    color: '#FFFAFA',
  },
  
  // Step indicator styles
  stepIndicatorContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
  },
  step: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: '#4C5454',
    color: '#FFFAFA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 5px',
  },
  activeStep: {
    backgroundColor: '#D2BA7B',
    color: '#243444',
  },
  stepLine: {
    height: '2px',
    width: '50px',
    backgroundColor: '#4C5454',
  },
  activeStepLine: {
    backgroundColor: '#D2BA7B',
  },
  
  // Tab styles
  tabs: {
    display: 'flex',
    marginBottom: '20px',
    borderBottom: '1px solid #ccc',
  },
  tabButton: {
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#4C5454',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease',
  },
  activeTabButton: {
    color: '#243444',
    fontWeight: 'bold',
    borderBottom: '3px solid #D2BA7B',
  },
  
  // Template selection styles
  templateSelectionContainer: {
    display: 'flex',
    gap: '30px',
    marginTop: '20px',
    height: '500px', // Taller container for templates
  },
  templateListContainer: {
    flex: '1',
    height: '100%',
    overflowY: 'auto',
    padding: '15px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  industryGroup: {
    marginBottom: '20px',
  },
  industryTitle: {
    color: '#243444',
    borderBottom: '1px solid #D2BA7B',
    paddingBottom: '5px',
    marginBottom: '10px',
  },
  templatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '15px',
  },
  templateCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#FFFAFA',
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '180px',
  },
  selectedTemplateCard: {
    border: '2px solid #D2BA7B',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  templateTitle: {
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#243444',
    fontSize: '16px',
  },
  templateDescription: {
    fontSize: '14px',
    color: '#4C5454',
    marginBottom: '5px',
    lineHeight: '1.4',
    maxHeight: '40px',
    overflow: 'hidden',
  },
  standardsContainer: {
    marginBottom: '5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxHeight: '20px',
  },
  standardsLabel: {
    fontWeight: 'bold',
    fontSize: '12px',
    color: '#243444',
    marginRight: '5px',
  },
  standardsList: {
    fontSize: '12px',
    color: '#4C5454',
  },
  templateMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: '#4C5454',
    marginTop: 'auto',
  },
  templateIndustry: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    display: 'inline-block',
  },
  templateQuestions: {
    backgroundColor: '#D2BA7B',
    color: '#243444',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block',
  },
  
  // Template details styles
  detailsContainer: {
    flex: '1',
    height: '100%',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '20px',
    backgroundColor: '#FFFAFA',
    overflowY: 'auto',
  },
  detailsTitle: {
    color: '#243444',
    borderBottom: '1px solid #D2BA7B',
    paddingBottom: '5px',
    marginBottom: '15px',
  },
  detailsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  detailsSection: {
    marginBottom: '10px',
  },
  detailsSectionTitle: {
    color: '#243444',
    margin: '0 0 5px 0',
    fontSize: '16px',
  },
  detailsText: {
    color: '#4C5454',
    margin: 0,
  },
  
  // User selection styles
  userSelectionContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '20px',
    maxHeight: '450px',
    overflowY: 'auto',
    padding: '15px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  userCard: {
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  selectedUserCard: {
    border: '2px solid #D2BA7B',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  userName: {
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#243444',
  },
  userEmail: {
    fontSize: '12px',
    color: '#4C5454',
  },
  
  // Error and loading styles
  error: {
    color: '#7C1227',
    padding: '10px',
    borderRadius: '4px',
    backgroundColor: 'rgba(124, 18, 39, 0.1)',
    marginBottom: '15px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  
  // No templates message
  noTemplatesMessage: {
    textAlign: 'center',
    padding: '20px',
    color: '#4C5454',
  },
  
  // Placeholder styles
  detailsPlaceholder: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: '#4C5454',
    fontStyle: 'italic',
  }
};
